import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { validate as validateEmail } from "email-validator";
import "react-phone-number-input/style.css";

import { extractErrorMessage } from "../utils/errorHandler";
import {
   ERR_PHONE_INVALID,
   ERR_EMAIL_INVALID,
   ERR_RESEND_CODE_FAILED,
   ERR_PHONE_REQUIRED_FOR_RESEND,
   ERR_PASSWORD_REQUIREMENTS,
   ERR_PASSWORDS_DO_NOT_MATCH,
   ERR_CREATE_ACCOUNT_FAILED,
   ERR_REQUIRED_FIELDS,
   ERR_ENTER_6_DIGIT_CODE,
   ERR_INVALID_VERIFICATION_CODE,
} from "../utils/errorMessages.js";

import "../styles/auth.css";
import "../styles/forms/phone-input.css";

const ConsequencesModal = ({ isOpen, onSkip, onCreateAccount }) => {
   if (!isOpen) return null;

   return (
      <div className="auth-modal-overlay">
         <div className="consequences-modal">
            <div className="consequences-header">
               <h3>
                  <svg
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24"
                     style={{ width: "20px", height: "20px" }}
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.33 15.5c-.77.833.192 2.5 1.732 2.5z"
                     />
                  </svg>
                  Are you sure you want to skip creating an account?
               </h3>
            </div>

            <div className="consequences-body">
               <p style={{ marginBottom: "16px", color: "#374151" }}>
                  Without an account, you will experience the following inconveniences:
               </p>

               <ul className="consequences-list">
                  <li>
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                     You'll have to re-enter all your personal information for every verification
                  </li>
                  <li>
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                     You'll need to upload documents and photos every time
                  </li>
                  <li>
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                     You'll go through the complete identity verification process each time
                  </li>
                  <li>
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                     No access to saved preferences or verification history
                  </li>
                  <li>
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                     Longer processing times for future load assignments
                  </li>
               </ul>
            </div>

            <div className="consequences-footer">
               <button className="auth-btn-danger" onClick={onSkip}>
                  Yes, Skip It Anyway
               </button>
               <button className="auth-btn-primary" onClick={onCreateAccount} style={{ margin: 0 }}>
                  Go Back & Create Account
               </button>
            </div>
         </div>
      </div>
   );
};

const SignupModal = ({
   isOpen,
   onClose,
   onSignupSuccess,
   onSkipAccount,
   driverData, // Pre-filled data from the form
}) => {
   const [createAccount, setCreateAccount] = useState(true);
   const [step, setStep] = useState("signup"); // 'signup' or 'otp'
   const [formData, setFormData] = useState({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
   });
   const [skipConfirmed, setSkipConfirmed] = useState(false);
   const [showConsequences, setShowConsequences] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState("");
   const [passwordErrors, setPasswordErrors] = useState([]);

   // OTP related state
   const [otp, setOtp] = useState("");
   const [otpLoading, setOtpLoading] = useState(false);
   const [resendTimer, setResendTimer] = useState(0);
   const [canResend, setCanResend] = useState(false);

   const modalRef = useRef();
   const modalBodyRef = useRef();
   const otpInputRef = useRef(null);

   // Use auth context
   const { signUp, verifyOTP, sendOTP } = useAuth();

   // Pre-fill form data when modal opens
   useEffect(() => {
      if (isOpen && driverData) {
         setFormData((prev) => ({
            ...prev,
            firstName: driverData.driverFirstName || "",
            lastName: driverData.driverLastName || "",
            email: driverData.driverEmail || "",
            phone: driverData.driverPhone || "",
         }));
      }
   }, [isOpen, driverData]);

   // Reset modal state when closed
   useEffect(() => {
      if (!isOpen) {
         setCreateAccount(true);
         setStep("signup");
         setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            password: "",
            confirmPassword: "",
         });
         setSkipConfirmed(false);
         setShowConsequences(false);
         setError("");
         setPasswordErrors([]);
         setIsLoading(false);
         // Reset OTP state
         setOtp("");
         setOtpLoading(false);
         setResendTimer(0);
         setCanResend(false);
      }
   }, [isOpen]);

   // OTP Timer
   useEffect(() => {
      let interval = null;
      if (resendTimer > 0) {
         interval = setInterval(() => {
            setResendTimer(resendTimer - 1);
         }, 1000);
      } else if (resendTimer === 0 && step === "otp") {
         setCanResend(true);
      }
      return () => clearInterval(interval);
   }, [resendTimer, step]);

   // Auto-focus first OTP input when step changes to OTP
   useEffect(() => {
      if (step === "otp" && otpInputRef.current) {
         setTimeout(() => {
            otpInputRef.current.focus();
         }, 100);
      }
   }, [step]);

   // Close modal on escape key
   useEffect(() => {
      const handleEscape = (e) => {
         if (e.key === "Escape" && isOpen && !showConsequences) {
            onClose();
         }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
   }, [isOpen, onClose, showConsequences]);

   // Close modal on outside click
   useEffect(() => {
      const handleClickOutside = (e) => {
         if (modalRef.current && !modalRef.current.contains(e.target) && !showConsequences) {
            onClose();
         }
      };
      if (isOpen) {
         document.addEventListener("mousedown", handleClickOutside);
      }
      return () => document.removeEventListener("mousedown", handleClickOutside);
   }, [isOpen, onClose, showConsequences]);

   // Auto-scroll to top when error appears
   useEffect(() => {
      if (error && modalBodyRef.current) {
         modalBodyRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
         });
      }
   }, [error]);

   const validatePassword = (password) => {
      const errors = [];
      if (password.length < 8) {
         errors.push("At least 8 characters");
      }
      if (!/(?=.*[a-z])/.test(password)) {
         errors.push("At least one lowercase letter");
      }
      if (!/(?=.*[A-Z])/.test(password)) {
         errors.push("At least one uppercase letter");
      }
      if (!/(?=.*\d)/.test(password)) {
         errors.push("At least one number");
      }
      return errors;
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));

      // Real-time password validation
      if (name === "password") {
         setPasswordErrors(validatePassword(value));
      }

      setError("");
   };

   const handlePhoneChange = (value) => {
      setFormData((prev) => ({
         ...prev,
         phone: value || "",
      }));
      setError("");
   };

   const handleToggleAccount = () => {
      setCreateAccount(!createAccount);
      setSkipConfirmed(false);
      setError("");
   };

   const handleSkipConfirmation = (e) => {
      setSkipConfirmed(e.target.checked);
      setError("");
   };

   // OTP Input Handlers
   const handleOtpChange = (e) => {
      const { value } = e.target;
      if (/^\d{0,6}$/.test(value)) {
         setOtp(value);
         setError("");
      }
   };

   const handleOtpKeyDown = (e) => {
      if (e.key === "Enter" && otp.length === 6) {
         handleOtpSubmit();
      }
   };

   const startResendTimer = () => {
      setResendTimer(30);
      setCanResend(false);
   };

   const handleResendOtp = async () => {
      try {
         setError("");

         // Call the resend OTP API with the phone number from form
         console.log("📱 Resending OTP to:", formData.phone);
         if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
            setError(ERR_PHONE_REQUIRED_FOR_RESEND);
            return;
         }
         const response = await sendOTP(formData.phone);

         if (response.success) {
            console.log("✅ OTP resent successfully");
            startResendTimer();
         } else {
            throw new Error(response.message || ERR_RESEND_CODE_FAILED);
         }
      } catch (err) {
         console.error("Resend OTP error:", err);
         const errorMessage = extractErrorMessage(err, ERR_RESEND_CODE_FAILED);
         setError(errorMessage);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setError("");

      if (createAccount) {
         // Validate account creation
         if (!formData.email || !validateEmail(formData.email)) {
            setError(ERR_EMAIL_INVALID);
            return;
         }

         if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
            setError(ERR_PHONE_INVALID);
            return;
         }

         const passwordValidationErrors = validatePassword(formData.password);
         if (passwordValidationErrors.length > 0) {
            setError(ERR_PASSWORD_REQUIREMENTS);
            return;
         }

         if (formData.password !== formData.confirmPassword) {
            setError(ERR_PASSWORDS_DO_NOT_MATCH);
            return;
         }

         setIsLoading(true);

         try {
            if (
               !formData.firstName ||
               !formData.lastName ||
               !formData.email ||
               !formData.phone ||
               !formData.password
            ) {
               throw new Error(ERR_REQUIRED_FIELDS);
            }

            // Call the signup API using auth context
            const response = await signUp({
               email: formData.email,
               phone: formData.phone,
               password: formData.password,
               first_name: formData.firstName,
               last_name: formData.lastName,
            });

            console.log("✅ Account created successfully");
            console.log("🔍 Signup response:", {
               otp_sent: response.otp_sent,
               requires_otp: response.requires_otp,
               account_created: response.account_created,
               message: response.message,
            });

            // Check if OTP was sent (new flow: signup → auto login → OTP)
            if (response.otp_sent && response.requires_otp) {
               console.log("📱 OTP sent after account creation, moving to verification");
               setStep("otp");
               setIsLoading(false);
               startResendTimer();
            } else {
               console.log("⚠️ No OTP required, completing signup directly");
               console.log("🔍 Fallback reason:", {
                  otp_sent: response.otp_sent,
                  requires_otp: response.requires_otp,
               });
               // Complete signup process directly (fallback case)
               onSignupSuccess({
                  ...response.user,
                  isAuthenticated: true,
                  accountCreated: true,
                  session: response.session,
               });
               onClose();
            }
         } catch (err) {
            const errorMessage = extractErrorMessage(err, ERR_CREATE_ACCOUNT_FAILED);
            setError(errorMessage);
            setIsLoading(false);
         }
      } else {
         // Handle skipping account creation
         if (!skipConfirmed) {
            setShowConsequences(true);
            return;
         }

         onSkipAccount();
         onClose();
      }
   };

   const handleOtpSubmit = async () => {
      const otpCode = otp;

      if (otpCode.length !== 6) {
         setError(ERR_ENTER_6_DIGIT_CODE);
         return;
      }

      setOtpLoading(true);
      setError("");

      try {
         // Call the OTP verification API using auth context
         console.log("🔍 Verifying OTP for phone:", formData.phone);
         const response = await verifyOTP(formData.phone, otpCode, "signup");

         console.log("✅ OTP verified:", otpCode);

         // Complete signup process
         onSignupSuccess({
            ...response.user,
            isAuthenticated: true,
            accountCreated: true,
            session: response.session,
         });
         onClose();
      } catch (err) {
         const errorMessage = extractErrorMessage(err, ERR_INVALID_VERIFICATION_CODE);
         setError(errorMessage);
      } finally {
         setOtpLoading(false);
      }
   };

   const handleConsequencesSkip = () => {
      setShowConsequences(false);
      onSkipAccount();
      onClose();
   };

   const handleConsequencesCreateAccount = () => {
      setShowConsequences(false);
      setCreateAccount(true);
      setSkipConfirmed(false);
   };

   if (!isOpen) return null;

   return (
      <>
         <div className="auth-modal-overlay">
            <div className="auth-modal signup-modal" ref={modalRef}>
               <div className="auth-modal-header">
                  <h2>{step === "otp" ? "Verify Your Phone" : "Complete Your Verification"}</h2>
                  <p className="auth-modal-subtitle">
                     {step === "otp"
                        ? `We sent a 6-digit code to ${formData.phone}`
                        : createAccount
                          ? "Create a free account to save time on future verifications"
                          : "Confirm that you want to skip creating an account"}
                  </p>
                  <button className="close-button" onClick={onClose} type="button">
                     ×
                  </button>
               </div>

               <div className="auth-modal-body" ref={modalBodyRef}>
                  {error && (
                     <div className="auth-error-message">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                           />
                        </svg>
                        {error}
                     </div>
                  )}

                  {step === "signup" && (
                     <div className="auth-toggle-container">
                        <span className="auth-toggle-label">Create an Account</span>
                        <label className="auth-toggle-switch">
                           <input
                              type="checkbox"
                              checked={createAccount}
                              onChange={handleToggleAccount}
                              className="auth-toggle-input"
                           />
                           <span className="auth-toggle-slider"></span>
                        </label>
                     </div>
                  )}

                  {step === "otp" ? (
                     <div className="auth-otp-section">
                        <div className="auth-otp-inputs">
                           <input
                              ref={otpInputRef}
                              type="text"
                              autoComplete="one-time-code"
                              inputMode="numeric"
                              value={otp}
                              onChange={handleOtpChange}
                              onKeyDown={handleOtpKeyDown}
                              className="auth-otp-input"
                              maxLength="6"
                              disabled={otpLoading}
                              placeholder="------"
                              style={{
                                 width: "100%",
                                 textAlign: "center",
                                 letterSpacing: "0.5em",
                                 fontSize: "1.2rem",
                              }}
                           />
                        </div>

                        <div className="auth-otp-actions">
                           <button
                              type="button"
                              onClick={handleOtpSubmit}
                              className="auth-btn-primary"
                              disabled={otp.length !== 6 || otpLoading}
                           >
                              {otpLoading ? (
                                 <div className="auth-loading">
                                    <div className="auth-spinner"></div>
                                    Verifying...
                                 </div>
                              ) : (
                                 <>
                                    <svg
                                       fill="none"
                                       stroke="currentColor"
                                       viewBox="0 0 24 24"
                                       style={{ width: "16px", height: "16px" }}
                                    >
                                       <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                       />
                                    </svg>
                                    Verify & Create Account
                                 </>
                              )}
                           </button>

                           <div className="auth-otp-resend">
                              {resendTimer > 0 ? (
                                 <span className="auth-timer">Resend code in {resendTimer}s</span>
                              ) : (
                                 <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    className="auth-link-button"
                                    disabled={!canResend}
                                 >
                                    Didn't receive the code? Send again
                                 </button>
                              )}
                           </div>

                           <button
                              type="button"
                              onClick={() => setStep("signup")}
                              className="auth-link-button"
                              style={{ marginTop: "12px" }}
                           >
                              <svg
                                 fill="none"
                                 stroke="currentColor"
                                 viewBox="0 0 24 24"
                                 style={{ width: "14px", height: "14px" }}
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 19l-7-7 7-7"
                                 />
                              </svg>
                              Back to signup
                           </button>
                        </div>
                     </div>
                  ) : (
                     <form onSubmit={handleSubmit}>
                        {createAccount ? (
                           <>
                              <div className="auth-name-fields">
                                 <div className="auth-form-group auth-name-field">
                                    <label htmlFor="firstName">
                                       First Name <span className="required">*</span>
                                    </label>
                                    <input
                                       type="text"
                                       id="firstName"
                                       name="firstName"
                                       value={formData.firstName}
                                       onChange={handleInputChange}
                                       placeholder="John"
                                       required
                                       disabled={isLoading}
                                    />
                                 </div>
                                 <div className="auth-form-group auth-name-field">
                                    <label htmlFor="lastName">
                                       Last Name <span className="required">*</span>
                                    </label>
                                    <input
                                       type="text"
                                       id="lastName"
                                       name="lastName"
                                       value={formData.lastName}
                                       onChange={handleInputChange}
                                       placeholder="Doe"
                                       required
                                       disabled={isLoading}
                                    />
                                 </div>
                              </div>

                              <div className="auth-form-group">
                                 <label htmlFor="email">
                                    Email Address <span className="required">*</span>
                                 </label>
                                 <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="john@example.com"
                                    required
                                    disabled={isLoading}
                                 />
                              </div>

                              <div className="auth-form-group">
                                 <label htmlFor="phone">
                                    Phone Number <span className="required">*</span>
                                 </label>
                                 <PhoneInput
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    placeholder="(555) 123-4567"
                                    defaultCountry="US"
                                    limitMaxLength={true}
                                    required
                                    disabled={isLoading}
                                 />
                              </div>

                              <div className="auth-form-group">
                                 <label htmlFor="password">
                                    Password <span className="required">*</span>
                                 </label>
                                 <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="Create a secure password"
                                    required
                                    disabled={isLoading}
                                 />
                                 {passwordErrors.length > 0 && (
                                    <div
                                       style={{
                                          fontSize: "12px",
                                          color: "#dc2626",
                                          marginTop: "4px",
                                       }}
                                    >
                                       Password must have:
                                       <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>
                                          {passwordErrors.map((error, index) => (
                                             <li key={index}>{error}</li>
                                          ))}
                                       </ul>
                                    </div>
                                 )}
                              </div>

                              <div className="auth-form-group">
                                 <label htmlFor="confirmPassword">
                                    Confirm Password <span className="required">*</span>
                                 </label>
                                 <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder="Confirm your password"
                                    required
                                    disabled={isLoading}
                                 />
                              </div>
                           </>
                        ) : (
                           <div className="auth-checkbox-wrapper">
                              <input
                                 type="checkbox"
                                 id="skipConfirm"
                                 checked={skipConfirmed}
                                 onChange={handleSkipConfirmation}
                                 required
                              />
                              <label htmlFor="skipConfirm">
                                 I understand I will have to go through the full verification
                                 process next time. <span className="required">*</span>
                              </label>
                           </div>
                        )}

                        <button type="submit" className="auth-btn-primary" disabled={isLoading}>
                           {isLoading ? (
                              <div className="auth-loading">
                                 <div className="auth-spinner"></div>
                                 {createAccount ? "Creating Account..." : "Processing..."}
                              </div>
                           ) : (
                              <>
                                 {createAccount ? (
                                    <>
                                       <svg
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                          style={{ width: "16px", height: "16px" }}
                                       >
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth="2"
                                             d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                          />
                                       </svg>
                                       Create Account & Submit
                                    </>
                                 ) : (
                                    <>
                                       <svg
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                          style={{ width: "16px", height: "16px" }}
                                       >
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth="2"
                                             d="M13 7l5 5m0 0l-5 5m5-5H6"
                                          />
                                       </svg>
                                       Skip & Submit Form
                                    </>
                                 )}
                              </>
                           )}
                        </button>
                     </form>
                  )}
               </div>
            </div>
         </div>

         <ConsequencesModal
            isOpen={showConsequences}
            onSkip={handleConsequencesSkip}
            onCreateAccount={handleConsequencesCreateAccount}
         />
      </>
   );
};

export default SignupModal;
